import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Typography,
} from "@mui/material";
import {
  BarChart as BarChartIcon,
  GridView as GridViewIcon,
  Search as SearchIcon,
  TableRows as TableRowsIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import AppGrowthService from "../services/AppGrowthService";
import AppService from "../services/AppService";
import { App } from "../types/app";
import { AppGrowthResponse, AppGrowthSnapshot } from "../types/appGrowth";
import { useAuth } from "../hooks/useAuth";

const PERIOD_OPTIONS = [7, 14, 30, 60, 90, 180] as const;
const ALL_APPS_VALUE = "__all_apps__";
type ChartMode = "total" | "platform";
type ViewMode = "cards" | "table";
type SortDirection = "asc" | "desc";
type AppSortKey =
  | "appName"
  | "platform"
  | "ga4PropertyId"
  | "totalNewUsers"
  | "previousNewUsers"
  | "change"
  | "changePercent"
  | "totalRevenue"
  | "previousRevenue"
  | "revenueChange"
  | "status";

const PLATFORM_COLORS = {
  ios: "#0f766e",
  android: "#4f8df7",
  other: "#94a3b8",
  unknown: "#cbd5e1",
};

const formatCount = (value: number): string =>
  new Intl.NumberFormat().format(Math.round(Number(value || 0)));

const formatRevenue = (value: number): string =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatPercent = (value: number | null): string => {
  if (value === null || !Number.isFinite(value)) return "New baseline";
  return `${value >= 0 ? "+" : ""}${value.toFixed(1)}%`;
};

const formatAppName = (value: string): string =>
  String(value || "").replace(/:\s*/g, ": ").trim();

const formatDateShort = (value: string): string => {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
    }).format(new Date(`${value}T00:00:00Z`));
  } catch {
    return value;
  }
};

const formatDateTime = (value: string): string => {
  if (!value) return "-";
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const growthColor = (value: number | null) => {
  if (value === null) return "info";
  if (value > 0) return "success";
  if (value < 0) return "error";
  return "default";
};

const metricCardSx = {
  p: 2,
  height: "100%",
  minHeight: 132,
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
};

const AppGrowth: React.FC = () => {
  const { user } = useAuth();
  const [periodDays, setPeriodDays] = useState<number>(7);
  const [appsCatalog, setAppsCatalog] = useState<App[]>([]);
  const [appScope, setAppScope] = useState<string>(ALL_APPS_VALUE);
  const [loadingApps, setLoadingApps] = useState(false);
  const [loadingGrowth, setLoadingGrowth] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [growth, setGrowth] = useState<AppGrowthResponse | null>(null);
  const [chartMode, setChartMode] = useState<ChartMode>("total");
  const [viewMode, setViewMode] = useState<ViewMode>("cards");
  const [appSearch, setAppSearch] = useState("");
  const [sortKey, setSortKey] = useState<AppSortKey>("totalNewUsers");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const selectedAppIds = useMemo(
    () => (appScope === ALL_APPS_VALUE ? [] : [appScope]),
    [appScope],
  );

  const readyApps = useMemo(
    () => (growth?.apps || []).filter((app) => app.status === "ready"),
    [growth],
  );

  const sortedApps = useMemo(() => {
    const apps = [...(growth?.apps || [])];
    return apps.sort((a, b) => Number(b.totalNewUsers || 0) - Number(a.totalNewUsers || 0));
  }, [growth]);

  const tableApps = useMemo(() => {
    const query = appSearch.trim().toLocaleLowerCase();
    const apps = (growth?.apps || []).filter((app) => {
      if (!query) return true;
      return [
        app.appName,
        app.platform,
        app.ga4PropertyId,
        app.ga4StreamId,
        app.status,
      ].some((value) => String(value || "").toLocaleLowerCase().includes(query));
    });

    return apps.sort((a, b) => {
      const aValue = a[sortKey];
      const bValue = b[sortKey];
      const comparison =
        typeof aValue === "number" || typeof bValue === "number"
          ? Number(aValue ?? Number.NEGATIVE_INFINITY) -
            Number(bValue ?? Number.NEGATIVE_INFINITY)
          : String(aValue || "").localeCompare(String(bValue || ""), undefined, {
              numeric: true,
              sensitivity: "base",
            });
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [appSearch, growth, sortDirection, sortKey]);

  const handleSort = (key: AppSortKey) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection(key === "appName" || key === "platform" ? "asc" : "desc");
  };

  const maxDailyValue = useMemo(() => {
    const values = readyApps.flatMap((app) =>
      app.daily.map((point) => Number(point.newUsers || 0)),
    );
    return Math.max(1, ...values);
  }, [readyApps]);

  const hasPlatformBreakdown = useMemo(
    () =>
      readyApps.some((app) =>
        app.daily.some((point) => {
          const breakdown = point.platformBreakdown || {};
          return (
            Number(breakdown.ios || 0) > 0 ||
            Number(breakdown.android || 0) > 0 ||
            Number(breakdown.other || 0) > 0
          );
        }),
      ),
    [readyApps],
  );

  const loadRegisteredApps = React.useCallback(async () => {
    if (!user?.email) return;
    setLoadingApps(true);
    setError(null);
    try {
      const result = await AppService.getUserApps(user.email);
      const analyticsApps = result.filter((app) =>
        Boolean(String(app.ga4PropertyId || "").trim()),
      );
      setAppsCatalog(analyticsApps);
      setAppScope((current) => {
        if (current === ALL_APPS_VALUE) return current;
        return analyticsApps.some((app) => app.appId === current)
          ? current
          : ALL_APPS_VALUE;
      });
    } catch (loadError: any) {
      setError(loadError?.message || "Failed to load registered apps.");
    } finally {
      setLoadingApps(false);
    }
  }, [user?.email]);

  React.useEffect(() => {
    loadRegisteredApps();
  }, [loadRegisteredApps]);

  const fetchGrowth = async () => {
    setLoadingGrowth(true);
    setError(null);
    try {
      const result = await AppGrowthService.fetchGrowth(
        periodDays,
        selectedAppIds,
      );
      setGrowth(result);
    } catch (loadError: any) {
      setError(loadError?.message || "Failed to fetch app growth data.");
    } finally {
      setLoadingGrowth(false);
    }
  };

  const renderDailyBars = (app: AppGrowthSnapshot) => {
    const firstPoint = app.daily[0];
    const lastPoint = app.daily[app.daily.length - 1];

    return (
      <Stack spacing={0.75}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${Math.max(app.daily.length, 1)}, minmax(12px, 1fr))`,
            alignItems: "end",
            gap: 0.75,
            height: 112,
            px: 0.25,
            pt: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          {app.daily.map((point) => {
            const height = Math.max(
              8,
              (Number(point.newUsers || 0) / maxDailyValue) * 96,
            );
            const breakdown = point.platformBreakdown || {};
            const iosUsers = Number(breakdown.ios || 0);
            const androidUsers = Number(breakdown.android || 0);
            const otherUsers =
              Number(breakdown.other || 0) + Number(breakdown.unknown || 0);
            const hasBreakdownForPoint =
              chartMode === "platform" &&
              (iosUsers > 0 || androidUsers > 0 || otherUsers > 0);
            const segment = (value: number, color: string) =>
              value > 0 ? (
                <Box
                  sx={{
                    height: `${Math.max(4, (value / Number(point.newUsers || 1)) * 100)}%`,
                    bgcolor: color,
                  }}
                />
              ) : null;

            return (
              <Tooltip
                key={`${app.appId}_${point.date}`}
                title={
                  hasBreakdownForPoint
                    ? `${formatDateShort(point.date)}: ${formatCount(
                        point.newUsers,
                      )} total. iOS ${formatCount(iosUsers)}, Android ${formatCount(
                        androidUsers,
                      )}${otherUsers ? `, Other ${formatCount(otherUsers)}` : ""}`
                    : `${formatDateShort(point.date)}: ${formatCount(
                        point.newUsers,
                      )} new users`
                }
                arrow
              >
                <Box
                  sx={{
                    height,
                    minWidth: 10,
                    borderRadius: "5px 5px 0 0",
                    bgcolor: "primary.main",
                    opacity: 0.82,
                    transition: "opacity 160ms ease",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column-reverse",
                    "&:hover": { opacity: 1 },
                  }}
                >
                  {hasBreakdownForPoint ? (
                    <>
                      {segment(iosUsers, PLATFORM_COLORS.ios)}
                      {segment(androidUsers, PLATFORM_COLORS.android)}
                      {segment(otherUsers, PLATFORM_COLORS.other)}
                    </>
                  ) : null}
                </Box>
              </Tooltip>
            );
          })}
        </Box>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">
            {formatDateShort(firstPoint?.date || "")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDateShort(lastPoint?.date || "")}
          </Typography>
        </Stack>
      </Stack>
    );
  };

  const sortableHeader = (
    label: string,
    key: AppSortKey,
    align: "left" | "right" = "left",
  ) => (
    <TableCell
      align={align}
      sortDirection={sortKey === key ? sortDirection : false}
      sx={{ whiteSpace: "nowrap", fontWeight: 800 }}
    >
      <TableSortLabel
        active={sortKey === key}
        direction={sortKey === key ? sortDirection : "asc"}
        onClick={() => handleSort(key)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "none",
        minWidth: 0,
        overflowX: "hidden",
      }}
    >
      <Paper
        sx={{
          p: { xs: 1.75, md: 2.25 },
          mb: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          background:
            "linear-gradient(135deg, rgba(14,116,144,0.1) 0%, rgba(255,255,255,1) 62%, rgba(34,197,94,0.08) 100%)",
        }}
      >
        <Stack
          direction={{ xs: "column", xl: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", xl: "center" }}
          justifyContent="space-between"
        >
          <Box sx={{ minWidth: 0, flex: { xs: "0 1 auto", xl: "1 1 420px" } }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TrendingUpIcon color="primary" />
              <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                App Growth
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              New users and revenue from GA4 for one app or the full portfolio.
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.75, display: "block" }}
            >
              Last fetched: {formatDateTime(growth?.fetchedAt || "")}
            </Typography>
          </Box>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", md: "center" }}
            sx={{
              flex: { xs: "0 1 auto", xl: "1 1 620px" },
              justifyContent: { xl: "flex-end" },
            }}
          >
            <TextField
              select
              size="small"
              label="App scope"
              value={appScope}
              onChange={(event) => setAppScope(String(event.target.value))}
              disabled={loadingApps}
              sx={{ minWidth: { xs: 1, md: 280, xl: 360 }, flex: { md: 1 } }}
            >
              <MenuItem value={ALL_APPS_VALUE}>All GA4-connected apps</MenuItem>
              {appsCatalog.map((app) => (
                <MenuItem key={app.appId} value={app.appId}>
                  {formatAppName(app.name)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Time period"
              value={periodDays}
              onChange={(event) => setPeriodDays(Number(event.target.value))}
              sx={{ minWidth: { xs: 1, md: 176 } }}
            >
              {PERIOD_OPTIONS.map((period) => (
                <MenuItem key={period} value={period}>
                  Last {period} days
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Chart"
              value={chartMode}
              onChange={(event) => setChartMode(event.target.value as ChartMode)}
              sx={{ minWidth: { xs: 1, md: 184 } }}
            >
              <MenuItem value="total">All downloads</MenuItem>
              <MenuItem value="platform">iOS / Android</MenuItem>
            </TextField>
            <Button
              variant="contained"
              startIcon={
                loadingGrowth ? <CircularProgress size={18} /> : <RefreshIcon />
              }
              onClick={fetchGrowth}
              disabled={loadingGrowth || loadingApps}
              sx={{ minWidth: { xs: 1, md: 168 }, height: 40 }}
            >
              Fetch Growth
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {growth && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} lg={3}>
            <Paper sx={metricCardSx}>
              <Typography variant="overline" color="text.secondary">
                New Users
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                {formatCount(growth.aggregate.totalNewUsers)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Last {growth.periodDays} days
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <Paper sx={metricCardSx}>
              <Typography variant="overline" color="text.secondary">
                Previous
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                {formatCount(growth.aggregate.previousNewUsers)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Previous {growth.periodDays} days
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <Paper sx={metricCardSx}>
              <Typography variant="overline" color="text.secondary">
                Growth
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {growth.aggregate.change >= 0 ? "+" : ""}
                  {formatCount(growth.aggregate.change)}
                </Typography>
                <Chip
                  size="small"
                  color={growthColor(growth.aggregate.changePercent) as any}
                  label={formatPercent(growth.aggregate.changePercent)}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                vs previous {growth.periodDays} days
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <Paper sx={metricCardSx}>
              <Typography variant="overline" color="text.secondary">
                Revenue
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                {formatRevenue(growth.aggregate.totalRevenue)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Last {growth.periodDays} days · USD
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {growth && (
        <Paper
          variant="outlined"
          sx={{ p: { xs: 1.25, sm: 1.5 }, mb: 2, borderRadius: 2 }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                App details
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {growth.apps.length} {growth.apps.length === 1 ? "app" : "apps"} in this report
              </Typography>
            </Box>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", sm: "center" }}
            >
              {viewMode === "table" && (
                <TextField
                  size="small"
                  value={appSearch}
                  onChange={(event) => setAppSearch(event.target.value)}
                  placeholder="Search apps, platform or property ID"
                  inputProps={{ "aria-label": "Search apps" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: { xs: "100%", sm: 320 } }}
                />
              )}
              <ToggleButtonGroup
                exclusive
                size="small"
                value={viewMode}
                onChange={(_, value: ViewMode | null) => value && setViewMode(value)}
                aria-label="App details view"
                sx={{ alignSelf: { xs: "stretch", sm: "auto" } }}
              >
                <ToggleButton value="cards" aria-label="Card view" sx={{ flex: 1 }}>
                  <GridViewIcon fontSize="small" sx={{ mr: 0.75 }} />
                  Cards
                </ToggleButton>
                <ToggleButton value="table" aria-label="Table view" sx={{ flex: 1 }}>
                  <TableRowsIcon fontSize="small" sx={{ mr: 0.75 }} />
                  Table
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Stack>
        </Paper>
      )}

      {viewMode === "cards" && (
        <Grid container spacing={2}>
          {sortedApps.map((app) => (
          <Grid item xs={12} lg={6} key={app.appId}>
            <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
              <CardContent>
                <Stack spacing={1.75}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        {formatAppName(app.appName)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {app.ga4PropertyId
                          ? `GA4 property ${app.ga4PropertyId}`
                          : "GA4 property not configured"}
                      </Typography>
                    </Box>
                    {app.status !== "ready" && (
                      <Chip
                        size="small"
                        color={app.status === "error" ? "error" : "warning"}
                        label={app.status}
                      />
                    )}
                  </Stack>

                  {app.status === "ready" ? (
                    <>
                      <Grid container spacing={1}>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">
                            New users
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            {formatCount(app.totalNewUsers)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">
                            Previous
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            {formatCount(app.previousNewUsers)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">
                            Change
                          </Typography>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>
                              {app.change >= 0 ? "+" : ""}
                              {formatCount(app.change)}
                            </Typography>
                            <Chip
                              size="small"
                              color={growthColor(app.changePercent) as any}
                              label={formatPercent(app.changePercent)}
                            />
                          </Stack>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">
                            Revenue (USD)
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            {formatRevenue(app.totalRevenue)}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Stack spacing={0.75}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={0.75}
                          alignItems={{ xs: "flex-start", sm: "center" }}
                          justifyContent="space-between"
                        >
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <BarChartIcon fontSize="small" color="primary" />
                            <Typography variant="caption" color="text.secondary">
                              {chartMode === "platform"
                                ? "Daily new users by platform"
                                : "Daily new users"}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {app.dateRange?.current?.startDate || ""} to{" "}
                            {app.dateRange?.current?.endDate || ""}
                          </Typography>
                        </Stack>
                        {chartMode === "platform" && (
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                            flexWrap="wrap"
                          >
                            {[
                              ["iOS", PLATFORM_COLORS.ios],
                              ["Android", PLATFORM_COLORS.android],
                              ["Other", PLATFORM_COLORS.other],
                            ].map(([label, color]) => (
                              <Stack
                                key={label}
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                              >
                                <Box
                                  sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    bgcolor: color,
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {label}
                                </Typography>
                              </Stack>
                            ))}
                            {!hasPlatformBreakdown && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Platform split appears when GA4 returns platform data.
                              </Typography>
                            )}
                          </Stack>
                        )}
                        {renderDailyBars(app)}
                      </Stack>
                    </>
                  ) : (
                    <Alert severity={app.status === "error" ? "error" : "warning"}>
                      {app.diagnostics?.[0] ||
                        "This app is missing the GA4 configuration required for growth reporting."}
                    </Alert>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          ))}
        </Grid>
      )}

      {growth && viewMode === "table" && (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
          <TableContainer
            sx={{
              maxWidth: "100%",
              overflowX: "auto",
              "&::-webkit-scrollbar": { height: 10 },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "action.disabled",
                borderRadius: 5,
              },
            }}
          >
            <Table
              size="small"
              aria-label="App growth details"
              sx={{ minWidth: 1420 }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: "action.hover",
                    "& th": { py: 1.5, borderBottomWidth: 2 },
                  }}
                >
                  {sortableHeader("App", "appName")}
                  {sortableHeader("Platform", "platform")}
                  {sortableHeader("GA4 property", "ga4PropertyId")}
                  {sortableHeader("New users", "totalNewUsers", "right")}
                  {sortableHeader("Previous", "previousNewUsers", "right")}
                  {sortableHeader("Change", "change", "right")}
                  {sortableHeader("Growth", "changePercent", "right")}
                  {sortableHeader("Revenue", "totalRevenue", "right")}
                  {sortableHeader("Prev. revenue", "previousRevenue", "right")}
                  {sortableHeader("Revenue change", "revenueChange", "right")}
                  <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 800 }}>
                    Date range
                  </TableCell>
                  {sortableHeader("Status", "status")}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableApps.map((app) => (
                  <TableRow
                    hover
                    key={app.appId}
                    sx={{
                      "&:last-child td": { borderBottom: 0 },
                      "& td": { py: 1.35 },
                    }}
                  >
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 1,
                        minWidth: 220,
                        bgcolor: "background.paper",
                        boxShadow: "4px 0 8px rgba(15, 23, 42, 0.04)",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {formatAppName(app.appName)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {app.appId}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textTransform: "capitalize" }}>
                      {app.platform || "—"}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {app.ga4PropertyId || "—"}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {formatCount(app.totalNewUsers)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCount(app.previousNewUsers)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 700,
                        color:
                          app.change > 0
                            ? "success.main"
                            : app.change < 0
                              ? "error.main"
                              : "text.primary",
                      }}
                    >
                      {app.change > 0 ? "+" : ""}
                      {formatCount(app.change)}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        size="small"
                        color={growthColor(app.changePercent) as any}
                        label={formatPercent(app.changePercent)}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>
                      {formatRevenue(app.totalRevenue)}
                    </TableCell>
                    <TableCell align="right">
                      {formatRevenue(app.previousRevenue)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 700,
                        color:
                          app.revenueChange > 0
                            ? "success.main"
                            : app.revenueChange < 0
                              ? "error.main"
                              : "text.primary",
                      }}
                    >
                      {app.revenueChange > 0 ? "+" : ""}
                      {formatRevenue(app.revenueChange)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {app.dateRange?.current
                        ? `${app.dateRange.current.startDate} – ${app.dateRange.current.endDate}`
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={app.diagnostics?.[0] || "GA4 data loaded"} arrow>
                        <Chip
                          size="small"
                          color={
                            app.status === "ready"
                              ? "success"
                              : app.status === "error"
                                ? "error"
                                : "warning"
                          }
                          label={app.status}
                          sx={{ textTransform: "capitalize" }}
                        />
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!tableApps.length && (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 6 }}>
                      <SearchIcon color="disabled" sx={{ fontSize: 36, mb: 1 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        No matching apps
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Try a different app name, platform, property ID, or status.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{
              px: 2,
              py: 1.25,
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: "action.hover",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Showing {tableApps.length} of {growth.apps.length} apps · Select a column heading to sort
            </Typography>
          </Box>
        </Paper>
      )}

      {!growth && !loadingGrowth && (
        <Alert severity="info">
          {appsCatalog.length
            ? "Choose a scope and fetch growth to load GA4 new-user and revenue data."
            : "No apps with a GA4 Property ID are available. Add one in App Registration to use this dashboard."}
        </Alert>
      )}
    </Box>
  );
};

export default AppGrowth;
